/**
 * Phase 6 cross-cutting PR 2 — `<BellTrigger>` · two render variants.
 *
 * **`'sidenav-row'`** (default · used by Default+ SideNav footer)
 * Labeled utility row matching the mockup's `.util.bell-row` pattern:
 *
 *   🔔  Notifications   [badge]
 *
 * **`'icon-button'`** (PR 1c-b · used by Mobile TopBar)
 * Compact 32px icon-only button matching TopBar's existing bell
 * affordance:
 *
 *   🔔   ← with optional badge over the top-right
 *
 * Both variants share the same data hooks (`useNotifications`,
 * `useAuthContext`), the same dropdown (`<NotifDropdown>`), and the
 * same escalation-modal handoff. Only the trigger markup + CSS
 * class differ.
 *
 * Renders nothing visible when the user is signed out (per vanilla
 * `hideNotificationBell()`).
 */

import { useState, useRef } from 'react';
import { useNotifications, type Notification } from '../../hooks/useNotifications';
import { useAuthContext } from '../../contexts/AuthContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { NotifDropdown } from './NotifDropdown';
import { NotificationsSheet } from './NotificationsSheet';
import { EscalationSummaryModal } from './EscalationSummaryModal';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import styles from './BellTrigger.module.css';

export type BellTriggerVariant = 'sidenav-row' | 'icon-button';

interface BellTriggerProps {
  /**
   * Render shape. `'sidenav-row'` (default) is the labeled utility
   * row for SideNav footer. `'icon-button'` is the compact 32px
   * icon used by Mobile's TopBar slot.
   */
  variant?: BellTriggerVariant;
}

export function BellTrigger({ variant = 'sidenav-row' }: BellTriggerProps = {}): JSX.Element | null {
  const { user } = useAuthContext();
  const notifications = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  // A2.1a — touch tiers (≤1023px) open a bottom-sheet; pointer tiers
  // (≥1024px) keep the bell-anchored dropdown. Same data, same actions,
  // same rows — only the container differs.
  const isTouchTier = useMediaQuery('(max-width: 1023px)');
  // PR 2b — escalation modal state. When an ai_escalation row is
  // clicked from the dropdown, NotifRow calls onEscalationClick
  // which sets this state; the modal then renders as a sibling of
  // the bell button (modal portals via its own fixed backdrop).
  const [escalation, setEscalation] = useState<Notification | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // CD-ICONS-01 — read before the signed-out early return (rules of hooks).
  const iconsOn = useIconSystem();

  // Signed out → no bell at all (matches vanilla `hideNotificationBell`).
  if (!user) return null;

  const { unreadCount } = notifications;
  const badgeText = unreadCount > 9 ? '9+' : String(unreadCount);
  const hasUnread = unreadCount > 0;
  const isIconButton = variant === 'icon-button';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={[
          isIconButton ? styles.iconButton : styles.row,
          hasUnread ? styles.hasUnread : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={hasUnread ? `Notifications · ${unreadCount} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
      >
        {/* CD-ICONS-01 — `nav.notify` (icons.json: "Top bar · notification
            list"), sized to each variant's existing box so the swap is a swap:
            16 in the SideNav row (`.glyph` is a 16×16 slot), 20 in the TopBar
            icon-button.

            ⛔ NO UNREAD DOT — R1 (CD-ICONS-02), founder-ratified B, "badge
            only". This lane originally mounted the ratified `var(--signal)`
            dot here and flagged that it collided with the numeric badge: two
            marks for one state, in almost the same place. The founder ruled for
            the BADGE. So the glyph is now plain in both states, `--signal`
            lives on `.badge`/`.iconBadge`'s background (it already did —
            `--badge-bg: var(--signal)`, no CSS change was needed), and the
            count keeps the information a dot could not carry.

            The unread STATE is still read below for the badge and the
            aria-label; it just no longer reaches the glyph. */}
        <span
          className={isIconButton ? styles.iconGlyph : styles.glyph}
          aria-hidden="true"
        >
          {iconsOn ? <Icon name="nav.notify" size={isIconButton ? 20 : 16} /> : '🔔'}
        </span>
        {!isIconButton ? <span className={styles.label}>Notifications</span> : null}
        {hasUnread ? (
          <span
            className={isIconButton ? styles.iconBadge : styles.badge}
            aria-hidden="true"
          >
            {badgeText}
          </span>
        ) : null}
      </button>
      {isOpen ? (
        isTouchTier ? (
          <NotificationsSheet
            notifications={notifications.notifications}
            unreadCount={notifications.unreadCount}
            onMarkAsRead={notifications.markAsRead}
            onMarkAllAsRead={notifications.markAllAsRead}
            onClose={() => setIsOpen(false)}
            onEscalationClick={(n) => {
              setIsOpen(false);
              setEscalation(n);
            }}
          />
        ) : (
          <NotifDropdown
            notifications={notifications.notifications}
            unreadCount={notifications.unreadCount}
            onMarkAsRead={notifications.markAsRead}
            onMarkAllAsRead={notifications.markAllAsRead}
            onClose={() => setIsOpen(false)}
            anchorRef={triggerRef}
            onEscalationClick={(n) => {
              // Dropdown closes; the modal takes over (preserves
              // caller-page context per direction.md §1 D4).
              setIsOpen(false);
              setEscalation(n);
            }}
          />
        )
      ) : null}
      {escalation ? (
        <EscalationSummaryModal
          title={escalation.title}
          message={escalation.message}
          onClose={() => setEscalation(null)}
        />
      ) : null}
    </>
  );
}
